import classNames from "classnames";
import { cva } from "cva";
import { ChangeEvent, ComponentPropsWithoutRef, InputHTMLAttributes } from "react";
import { Tooltip } from "react-tooltip";
import 'react-tooltip/dist/react-tooltip.css';
import { Label, QuestionIcon, Text } from "..";

type InputType = "text" | "password" | "email" | "number" | "date" | "time" | "datetime-local" | "search" | "tel" | "url"
export type BaseProps = React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>  & {
    inputClassName?: string
    labelClassName?: string
    tooltip?: string
    error?: boolean
    locked?: boolean
    isPreview?: boolean
    label?: string
}

type InputProps = BaseProps & Omit<InputHTMLAttributes<HTMLInputElement>, keyof BaseProps> & {
    type: InputType
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void | Function
}

type TextAreaProps = BaseProps & Omit<ComponentPropsWithoutRef<"textarea">, keyof BaseProps> & {
    type: "textarea"
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void | Function
}

type Props = InputProps | TextAreaProps

export const InputStyles = cva("w-full px-3 py-2.5 leading-6 focus:outline-none focus:ring-0 border bg-inherit", {
    variants: {
        state: {
            default: [
                "-border--component-colors-field-default-input-border -text--component-colors-field-filled-input-text placeholder:-text--component-colors-field-default-input-placeholder",
                "disabled:-border--component-colors-field-disabled-input-border placeholder:-text--component-colors-field-disabled-input-placeholder",
                "hover:-border--component-colors-field-hover-input-border -text--component-colors-field-hover-input-text placeholder:-text--component-colors-field-hover-input-placeholder",
                "focus:-border--component-colors-field-selected-input-border -rounded-input"
            ],
            error: "-border--component-colors-field-error-input-border -text--component-colors-field-error-input-text placeholder:-text--component-colors-field-error-input-placeholder  -rounded-input",
            locked: "-border--component-colors-field-locked-input-border -text--component-colors-field-locked-input-text placeholder:-text--component-colors-field-locked-input-placeholder  -rounded-input opacity-50",
        }
    },
    defaultVariants: {
        state: "default"
    }
})

const Input = (props: Props) => {
    const { type, placeholder, className, labelClassName, inputClassName, label, error, locked, isPreview, required, ...rest } = props
    return (
        <div className={classNames("w-full flex flex-col", className)}>
            <div className="w-full flex items-center justify-between ">
                {
                    label &&
                    <Label isPreview={isPreview} locked={locked} error={error} htmlFor={props.name} required={required} className={labelClassName}>{label}</Label>
                }
                {
                    props.tooltip &&
                    <div className="flex items-center justify-center cursor-pointer">
                        <span data-tooltip-id={`${label}-${type}`} data-tooltip-content={props.tooltip}>
                            <QuestionIcon width={20} height={20} />
                        </span>
                        <Tooltip place={"right"} className="bg-gray-800 max-w-[180px] text-xs text-white leading-6 p-1" id={`${label}-${type}`} />
                    </div>
                }
            </div>
            {isPreview ?
                <Text className="-text--theme-colors-text-body text-xl break-words whitespace-pre-wrap">
                    {props.value || placeholder || "-"}
                </Text>
                :
                type === "textarea" ?
                    <textarea {...rest as Partial<TextAreaProps>} className={classNames(InputStyles({ state: (error && "error") || (locked && "locked") || "default" }), { "mt-2.5": label }, inputClassName)} placeholder={placeholder} />
                    :
                    <input  {...rest as Partial<InputProps>} className={classNames(InputStyles({ state: (error && "error") || (locked && "locked") || "default" }), { "mt-2.5": label }, inputClassName)} type={type} placeholder={placeholder} />

            }
        </div>
    )
}

export default Input;